import React, { useState, useEffect, useRef } from 'react';
import { Layout, Menu, Avatar, Dropdown, theme, Space, Typography, Button, message, ConfigProvider, Modal } from 'antd';
import {
    UserOutlined,
    LogoutOutlined,
    MenuUnfoldOutlined,
    MenuFoldOutlined,
    DashboardOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { authService, pbxService } from '../services/api';
import * as AntdIcons from '@ant-design/icons';
import Softphone from '../components/Softphone';
import useLogout from '../hooks/useLogout';
import stcLogo from '../assets/stc-logo-300x316.png';

const { Header, Content, Footer, Sider } = Layout;
const { Text } = Typography;

const MainLayout = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [menuItems, setMenuItems] = useState([]);
    const { logout, user, permissions } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();
    const { handleLogout, isLoading: isLogoutLoading } = useLogout();
    // const isLoggingOut = useRef(false);

    // Dynamically load icon component
    const getIcon = (iconName) => {
        const IconComponent = AntdIcons[iconName];
        return IconComponent ? <IconComponent /> : <DashboardOutlined />;
    };

    // Build hierarchical menu
    const buildMenuTree = (items, parentId = null) => {
        return items
            .filter(item => item.parent_id === parentId)
            .filter(item => !item.required_permission || (permissions && permissions.includes(item.required_permission)))
            .map(item => {
                const children = buildMenuTree(items, item.id);
                const hasChildren = children.length > 0;
                return {
                    key: item.route || String(item.id),
                    icon: getIcon(item.icon),
                    label: item.route ? item.title : (
                        <span style={{ color: '#fff', fontWeight: 400 }}>{item.title}</span>
                    ),
                    route: item.route,
                    children: hasChildren ? children : null,
                };
            })
            .filter(item => item.children || item.route);
    };

    // Handle menu click with confirmation
    const handleMenuClick = async ({ key, domEvent }) => {
        const menuItem = findMenuItem(menuItems, key);
        if (!menuItem || !menuItem.route) return;

        // Prevent default navigation
        if (domEvent) domEvent.preventDefault();

        // If we're leaving Call Center, check status first
        if (location.pathname === '/callcenter' && menuItem.route !== '/callcenter') {
            try {
                const snapshot = await api.get('/pbx/agents/state_snapshot.php');
                if (snapshot.data.status === 'success') {
                    const currentStatus = snapshot.data.data.status;

                    // If status is NOT 'online', show confirmation
                    if (currentStatus !== 'online') {
                        Modal.confirm({
                            title: '¿Está seguro de salir del Call Center?',
                            icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
                            content: (
                                <div>
                                    <p>Al salir del módulo de Call Center, su estado cambiará a <strong>NO DISPONIBLE</strong> y dejará de recibir llamadas.</p>
                                    <p>¿Desea continuar?</p>
                                </div>
                            ),
                            okText: 'Sí, salir',
                            cancelText: 'Cancelar',
                            onOk: async () => {
                                // Change status to online and navigate
                                await api.post('/pbx/agents/status_update.php', { status: 'online' });
                                message.info('Estado cambiado a NO DISPONIBLE');
                                navigate(menuItem.route);
                            },
                            onCancel: () => {
                                // Do nothing, stay in Call Center
                            }
                        });
                        return;
                    }
                }
            } catch (e) {
                console.error("Error checking agent status", e);
            }
        }

        // Normal navigation
        navigate(menuItem.route);
    };

    // Helper to find menu item by key
    const findMenuItem = (items, key) => {
        for (const item of items) {
            if (item.key === key) return item;
            if (item.children) {
                const found = findMenuItem(item.children, key);
                if (found) return found;
            }
        }
        return null;
    };

    // Fetch navigation items
    useEffect(() => {
        const fetchNav = async () => {
            try {
                const response = await api.get('/navigation.php');
                if (response.data.status === 'success') {
                    const rawItems = response.data.data;
                    const formattedItems = buildMenuTree(rawItems);
                    setMenuItems(formattedItems);

                    // Update dynamic title
                    const currentItem = rawItems.find(item => item.route === location.pathname);
                    const moduleName = currentItem ? currentItem.title : 'Agente';
                    document.title = `Agente | ${moduleName} | Save the Children en Perú`;
                }
            } catch (error) {
                console.error("Error fetching navigation", error);
                message.error("Error al cargar el menú");
            }
        };

        if (user) {
            fetchNav();
        }
    }, [user]);

    // --- INACTIVITY TIMEOUT LOGIC ---
    // Rules:
    // 1. If in /callcenter, DO NOT TIMEOUT (Persistent session).
    // 2. Un other views, if inactive for X minutes, warn then logout.
    // 3. Logout must set status to 'offline'.

    // CONFIGURATION (Production: 30 minutes)
    const TIMEOUT_DURATION = 1800000; // 30 minutes
    const WARNING_THRESHOLD = 1740000; // Warn after 29 mins (60s countdown)

    // States
    const [lastActivity, setLastActivity] = useState(Date.now());
    const [showTimeoutModal, setShowTimeoutModal] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(20);

    // 1. Event Listener for Activity
    useEffect(() => {
        const updateActivity = () => {
            setLastActivity(Date.now());
            if (showTimeoutModal) {
                // Auto-dismiss if user interacts? 
                // Better to require explicit click on modal to be safe, 
                // but normally movement means "I'm here".
                // Let's NOT auto-dismiss on move to prevent accidental keep-alive if they just bumped desk.
                // Force them to click "Mantenerse Conectado".
            }
        };

        // Throttle/Debounce could be added, but native events are fine if logic is light
        const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

        // Only attach if NOT in callcenter to save resources? 
        // No, we want to track activity everywhere strictly speaking, 
        // but for the timeout logic we only care if NOT in callcenter.
        // Actually, if I move to CallCenter, I should update activity to reset the timer for when I leave.

        const throttleWrap = (func, delay) => {
            let lastCall = 0;
            return (...args) => {
                const now = new Date().getTime();
                if (now - lastCall < delay) return;
                lastCall = now;
                return func(...args);
            };
        };

        const throttledUpdate = throttleWrap(updateActivity, 1000);

        events.forEach(event => window.addEventListener(event, throttledUpdate));

        return () => {
            events.forEach(event => window.removeEventListener(event, throttledUpdate));
        };
    }, []);

    // 2. Timer Check & Location Reset
    // Reset timer on navigation to ensure we don't carry over old "inactivity"
    useEffect(() => {
        setLastActivity(Date.now());
        if (location.pathname === '/callcenter') {
            if (showTimeoutModal) setShowTimeoutModal(false);
        }
    }, [location.pathname]);

    useEffect(() => {
        // If we are in CallCenter, we are SAFE. logic handled by return.
        if (location.pathname === '/callcenter') return;

        const interval = setInterval(() => {
            // Recalculate based on current expected timeout
            const now = Date.now();
            const elapsed = now - lastActivity;

            if (elapsed > TIMEOUT_DURATION) {
                // EXPIRED
                handleInactivityLogout();
            } else if (elapsed > WARNING_THRESHOLD) {
                // WARNING ZONE
                if (!showTimeoutModal) {
                    setShowTimeoutModal(true);
                }
                setSecondsLeft(Math.ceil((TIMEOUT_DURATION - elapsed) / 1000));
            } else {
                // SAFE ZONE
                if (showTimeoutModal) setShowTimeoutModal(false);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [lastActivity, location.pathname, showTimeoutModal, TIMEOUT_DURATION, WARNING_THRESHOLD]);

    const handleKeepAlive = async () => {
        setLastActivity(Date.now());
        setShowTimeoutModal(false);
        try {
            await authService.pingSession();
        } catch (e) { console.error(e); }
    };

    const handleInactivityLogout = async () => {
        // Explicitly set offline as requested
        try {
            console.log("Inactivity Timeout: Setting status to offline...");
            // We use fetch directly or pbxService if available.
            // Using a raw fetch to ensure it fires even if contexts are tearing down, 
            // but pbxService is imported so let's use it relative to token.
            // NOTE: pbxService needs to be imported!
            const token = localStorage.getItem('token');
            if (token) {
                await fetch(`${import.meta.env.VITE_API_URL || 'https://api.devstc.ispcorp.pe'}/pbx/agents/status_update.php`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ status: 'offline' })
                });
            }
        } catch (e) {
            console.error("Error setting offline on timeout", e);
        }

        message.error("Sesión cerrada por inactividad.");
        logout(); // From useAuth
    };


    // --- 15-SECOND STRICT TIMEOUT RECOVERY CHECK ---
    useEffect(() => {
        const lastDisconnectStr = localStorage.getItem('stc_disconnect_ts');
        if (lastDisconnectStr) {
            const lastDisconnect = parseInt(lastDisconnectStr, 10);
            const elapsed = Date.now() - lastDisconnect;

            // Clean up the timestamp immediately after reading
            localStorage.removeItem('stc_disconnect_ts');

            console.log(`[Session Check] Last disconnect: ${new Date(lastDisconnect).toISOString()}, Elapsed: ${elapsed}ms`);

            // RULE: If absent more than 15 seconds, FORCE RE-LOGIN.
            if (elapsed > 15000) {
                console.warn("[Session Check] Timeout exceeded (> 15s). Forcing logout.");
                logout(); // This clears token and redirects to /login
                return;
            } else {
                console.log("[Session Check] < 15s. Resuming session (Resilience).");
            }
        }
    }, [logout]); // Runs once on mount (reload/open)

    // Session Ping & Heartbeat
    useEffect(() => {
        // Initial ping
        // authService.pingSession();  <-- Moved inside condition if needed, but safe here is token survives
        authService.pingSession();

        // Setup Heartbeat (every 30s)
        const heartbeatInterval = setInterval(() => {
            if (user) {
                api.post('/pbx/agents/heartbeat.php').catch(err => {
                    console.warn('Heartbeat failed', err);
                });
            }
        }, 30000);

        return () => clearInterval(heartbeatInterval);
    }, [location.pathname, user]);

    // Handle unexpected close / refresh
    useEffect(() => {
        // If logging out, do NOT attach manual exit listeners
        if (isLogoutLoading) return;

        const handleBeforeUnload = (event) => {
            // Trigger browser confirmation dialog ONLY if in Call Center
            // AND only for actual page unload (not React navigation/tab changes)
            if (location.pathname === '/callcenter') {
                // This will only fire on browser close, tab close, or refresh (F5)
                // NOT on React Router navigation or state changes
                event.preventDefault();
                event.returnValue = '';
            }
        };

        const handlePageHide = (event) => {
            const token = localStorage.getItem('token');
            // console.log('MainLayout: PageHide/Unload triggered. Token:', !!token);

            if (!token) return;

            // SAVE DISCONNECT TIMESTAMP FOR 15s RULE
            localStorage.setItem('stc_disconnect_ts', Date.now().toString());

            const body = JSON.stringify({ status: 'offline', reason: 'auto_disconnect' });
            const baseUrl = import.meta.env.VITE_API_URL || 'https://api.devstc.ispcorp.pe';
            const url = `${baseUrl}/pbx/agents/status_update.php?token=${token}`;

            fetch(url, {
                method: 'POST',
                headers: {},
                body: body,
                keepalive: true
            }).catch(err => console.error('Offline signal failed', err));
        };

        // Only add beforeunload when in callcenter
        if (location.pathname === '/callcenter') {
            window.addEventListener('beforeunload', handleBeforeUnload);
        }

        // Always monitor pagehide for session management
        window.addEventListener('pagehide', handlePageHide);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('pagehide', handlePageHide);
        };
    }, [location.pathname, isLogoutLoading]);




    // const isLoggingOut = useRef(false); // REMOVED: Managed by hook/state now

    const handleLogoutClick = () => {
        handleLogout();
    };

    const userDropdownItems = [
        {
            key: 'user-info',
            label: (
                <div style={{ padding: '8px 0' }}>
                    <Text strong>{user?.username || 'Usuario'}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>{user?.email || ''}</Text>
                </div>
            ),
            disabled: true
        },
        { type: 'divider' },
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: 'Cerrar Sesión',
            danger: true,
            onClick: handleLogoutClick
        }
    ];

    return (
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: '#DA291C',
                    borderRadius: 8,
                },
            }}
        >
            <Layout style={{ minHeight: '100vh' }}>
                <Sider
                    collapsible
                    collapsed={collapsed}
                    onCollapse={setCollapsed}
                    width={250}
                    style={{ background: '#000000' }}
                    trigger={null}
                >
                    <div style={{
                        height: 64,
                        margin: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: collapsed ? '18px' : '20px',
                        fontWeight: 'bold',
                        transition: 'all 0.3s',
                    }}>
                        {collapsed ? 'STC' : 'SAVE THE CHILDREN'}
                    </div>
                    <Menu
                        theme="dark"
                        mode="inline"
                        selectedKeys={[location.pathname]}
                        items={menuItems}
                        onClick={handleMenuClick}
                    />
                </Sider>
                <Layout>
                    <Header style={{
                        padding: '0 24px',
                        background: colorBgContainer,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                        <Space>
                            <Button
                                type="text"
                                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                                onClick={() => setCollapsed(!collapsed)}
                                style={{ fontSize: '16px', width: 64, height: 64 }}
                            />
                        </Space>
                        <Space size="large">
                            <Softphone />
                            <Dropdown menu={{ items: userDropdownItems }} trigger={['click']}>
                                <Space style={{ cursor: 'pointer', padding: '0 12px' }}>
                                    <Avatar
                                        src={stcLogo}
                                        style={{
                                            background: '#fff',
                                            padding: '4px',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}
                                    />
                                    <Text>{user?.username || 'Usuario'}</Text>
                                </Space>
                            </Dropdown>
                        </Space>
                    </Header>
                    <Content style={{ margin: '24px', marginBottom: '80px' }}>
                        <div
                            style={{
                                padding: 24,
                                minHeight: 360,
                                background: colorBgContainer,
                                borderRadius: borderRadiusLG,
                            }}
                        >
                            <Outlet />
                        </div>
                    </Content>
                    <Footer style={{ textAlign: 'center' }}>
                        Save The Children © 2026 - Power By ISP CORP | Call Center System
                    </Footer>
                </Layout>
            </Layout>

            {/* Inactivity Warning Modal */}
            <Modal
                title={
                    <Space>
                        <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                        <span>Inactividad Detectada</span>
                    </Space>
                }
                open={showTimeoutModal}
                onOk={handleKeepAlive}
                onCancel={handleKeepAlive} // Clicking X or mask also keeps alive
                okText="Mantenerse Conectado"
                cancelText="Cerrar Sesión"
                cancelButtonProps={{ danger: true }} // Visual cue that cancel = logout? No, actually let's make cancel = keep alive usually, or remove cancel.
                // Let's customize footer to be clear
                footer={[
                    <Button key="logout" danger onClick={() => handleInactivityLogout()}>
                        Salir ahora
                    </Button>,
                    <Button key="keep" type="primary" onClick={handleKeepAlive}>
                        Mantenerse Conectado ({secondsLeft}s)
                    </Button>
                ]}
                closable={false}
                maskClosable={false}
                centered
            >
                <p>Su sesión ha estado inactiva por un tiempo.</p>
                <p>Por seguridad, se cerrará la sesión automáticamente en <strong>{secondsLeft}</strong> segundos.</p>
                <p>¿Desea continuar trabajando?</p>
            </Modal>

        </ConfigProvider>
    );
};

export default MainLayout;
